import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  IconButton,
  Tooltip,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Divider,
  Checkbox,
  Collapse,
  Autocomplete,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  PauseCircle as PauseIcon,
  PlayCircle as PlayIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  School as CoachIcon,
  Business as ClubIcon,
  Badge as BadgeIcon,
  Cake as BirthdayIcon,
  Visibility as ViewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  VerifiedUser as VerifiedUserIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import { API_BASE_URL, REGISTRATION_BASE_URL } from '../config/environment';
import { ORDERED_SKATING_LEVELS } from '../utils/skatingLevels';

const ALL_LEVELS = ORDERED_SKATING_LEVELS;

const filterOptions = [
  { value: 'all', label: 'All Skaters' },
  { value: 'paid', label: 'Paid' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'duplicates', label: 'Duplicates only' },
  { value: 'active', label: 'Active for Season' },
  { value: 'paused', label: 'Paused for Season' },
];

export default function AdminSkaters() {
  const navigate = useNavigate();
  const { getIdToken, user, isInterclubAdmin, getInterclubAdminIds } = useFirebaseAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [skaters, setSkaters] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [interclubs, setInterclubs] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [seasonId, setSeasonId] = useState('');
  const [interclubId, setInterclubId] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Detail/Edit dialog state
  const [selectedSkater, setSelectedSkater] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    membershipId: '',
    currentLevel: '',
    clubId: '',
    coaches: [],
  });
  const [saving, setSaving] = useState(false);
  const [prePrelimAcknowledged, setPrePrelimAcknowledged] = useState(false);
  const [showOriginalAck, setShowOriginalAck] = useState(false);
  const [ownerInfo, setOwnerInfo] = useState(null);
  const [ownerLoading, setOwnerLoading] = useState(false);

  const loadSeasons = useCallback(async () => {
    const response = await fetch(`${API_BASE_URL}/seasons`);
    const data = await response.json();
    const sorted = (data || []).sort((a, b) => (a.startDate?._seconds || 0) - (b.startDate?._seconds || 0));
    setSeasons(sorted);
    const current = sorted.find((season) => season.isCurrent);
    if (current) setSeasonId(current.id);
  }, []);

  const loadInterclubs = useCallback(async () => {
    const response = await fetch(`${API_BASE_URL}/interclubs`);
    const data = await response.json();
    setInterclubs(data || []);
  }, []);

  const loadClubs = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/clubs`);
      const data = await response.json();
      setClubs(data || []);
    } catch (err) {
      console.error('Error loading clubs:', err);
    }
  }, []);

  const loadCoaches = useCallback(async () => {
    try {
      const token = await getIdToken(true);
      const response = await fetch(`${REGISTRATION_BASE_URL}/coaches?usfsOnly=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCoaches(data.coaches || []);
      }
    } catch (err) {
      console.error('Error loading coaches:', err);
    }
  }, [getIdToken]);

  const loadOwnerInfo = useCallback(async (userId) => {
    if (!userId) {
      setOwnerInfo(null);
      return;
    }
    try {
      setOwnerLoading(true);
      const token = await getIdToken(true);
      const response = await fetch(`${API_BASE_URL}/firebase-users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setOwnerInfo(data);
      } else {
        setOwnerInfo(null);
      }
    } catch (err) {
      console.error('Error loading owner info:', err);
      setOwnerInfo(null);
    } finally {
      setOwnerLoading(false);
    }
  }, [getIdToken]);

  const loadSkaters = useCallback(async () => {
    try {
      if (!seasonId) return;
      setLoading(true);
      setError(null);
      const token = await getIdToken(true);
      const params = new URLSearchParams({ seasonId });
      if (interclubId) params.set('interclubId', interclubId);
      const response = await fetch(`${API_BASE_URL}/admin/skaters?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to load skaters');
      }
      const data = await response.json();
      setSkaters(data.skaters || []);
    } catch (err) {
      console.error('Error loading skaters:', err);
      setError('Failed to load skaters');
    } finally {
      setLoading(false);
    }
  }, [seasonId, interclubId, getIdToken]);

  useEffect(() => {
    loadSeasons();
    loadInterclubs();
    loadClubs();
    loadCoaches();
  }, [loadSeasons, loadInterclubs, loadClubs, loadCoaches]);

  useEffect(() => {
    if (isInterclubAdmin()) {
      const ids = getInterclubAdminIds();
      if (ids.length > 0) setInterclubId(ids[0]);
    }
  }, [isInterclubAdmin, getInterclubAdminIds]);

  useEffect(() => {
    loadSkaters();
  }, [loadSkaters]);

  const handleSkaterClick = (skater) => {
    setSelectedSkater(skater);
    setEditMode(false);
    setShowOriginalAck(false);
    setDetailDialogOpen(true);
    // Load owner/parent info
    loadOwnerInfo(skater.userId);
  };

  const handleCloseDetail = () => {
    setDetailDialogOpen(false);
    setSelectedSkater(null);
    setEditMode(false);
    setOwnerInfo(null);
    setShowOriginalAck(false);
  };

  const handleEditClick = () => {
    if (!selectedSkater) return;
    setEditForm({
      firstName: selectedSkater.firstName || '',
      lastName: selectedSkater.lastName || '',
      membershipId: selectedSkater.membershipId || '',
      currentLevel: selectedSkater.currentLevel || selectedSkater.startingLevel || '',
      clubId: selectedSkater.clubId || '',
      coaches: selectedSkater.coaches || [],
    });
    // Only pre-check if they have an actual acknowledgment on file
    setPrePrelimAcknowledged(!!selectedSkater.prePrelimAcknowledgedAt);
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditForm({
      firstName: '',
      lastName: '',
      membershipId: '',
      currentLevel: '',
      clubId: '',
      coaches: [],
    });
    setPrePrelimAcknowledged(false);
  };

  const handleSaveEdit = async () => {
    if (!selectedSkater) return;

    try {
      setSaving(true);
      const token = await getIdToken(true);

      // Get club name for the selected clubId
      const selectedClubData = clubs.find(c => c.id === editForm.clubId);
      const clubName = selectedClubData?.name || '';

      // Build the update payload
      const updatePayload = {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        membershipId: editForm.membershipId,
        currentLevel: editForm.currentLevel,
        clubId: editForm.clubId,
        club: clubName,
        coaches: editForm.coaches,
        seasonId,
        // Pre-Preliminary acknowledgment tracking for audit purposes (only app admins can record acknowledgements)
        ...(editForm.currentLevel === 'Pre-Preliminary' && !isInterclubAdmin() && prePrelimAcknowledged && !selectedSkater.prePrelimAcknowledgedAt ? {
          prePrelimAcknowledgedAt: new Date().toISOString(),
          prePrelimAcknowledgedBy: user?.uid || null,
          prePrelimAcknowledgedByEmail: user?.email || null,
        } : {}),
      };

      const response = await fetch(`${REGISTRATION_BASE_URL}/skaters/${selectedSkater.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updatePayload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update skater');
      }

      setSnackbar({
        open: true,
        message: `${editForm.firstName} ${editForm.lastName} updated successfully`,
        severity: 'success',
      });
      handleCloseDetail();
      loadSkaters();
    } catch (err) {
      console.error('Error updating skater:', err);
      setSnackbar({ open: true, message: err.message || 'Failed to update skater', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSkater = async (skater) => {
    if (
      !window.confirm(
        `Are you sure you want to delete ${skater.firstName} ${skater.lastName}?\n\nThis will remove them from the parent's profile and they will no longer be visible to the user.\n\nIf you just want to pause them for the season, use the pause button instead.`
      )
    ) {
      return;
    }

    try {
      const token = await getIdToken(true);
      const response = await fetch(`${API_BASE_URL}/admin/skaters/${skater.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete skater');
      }

      setSnackbar({ open: true, message: `${skater.firstName} ${skater.lastName} deleted successfully`, severity: 'success' });
      loadSkaters();
    } catch (err) {
      console.error('Error deleting skater:', err);
      setSnackbar({ open: true, message: err.message || 'Failed to delete skater', severity: 'error' });
    }
  };

  const handleTogglePause = async (skater) => {
    const isPaused = (skater.pausedSeasons || []).includes(seasonId);
    const action = isPaused ? 'unpause' : 'pause';
    const actionLabel = isPaused ? 'activate' : 'pause';

    if (!window.confirm(`Are you sure you want to ${actionLabel} ${skater.firstName} ${skater.lastName} for this season?`)) {
      return;
    }

    try {
      const token = await getIdToken(true);
      const response = await fetch(`${API_BASE_URL}/admin/skaters/${skater.id}/${action}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ seasonId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${action} skater`);
      }

      setSnackbar({
        open: true,
        message: `${skater.firstName} ${skater.lastName} ${isPaused ? 'activated' : 'paused'} for this season`,
        severity: 'success',
      });
      loadSkaters();
    } catch (err) {
      console.error(`Error ${action}ing skater:`, err);
      setSnackbar({ open: true, message: err.message || `Failed to ${action} skater`, severity: 'error' });
    }
  };

  const filteredSkaters = useMemo(() => {
    return skaters.filter((skater) => {
      const isPaused = (skater.pausedSeasons || []).includes(seasonId);
      if (filter === 'paid' && !skater.paid) return false;
      if (filter === 'unpaid' && skater.paid) return false;
      if (filter === 'duplicates' && !skater.isDuplicate) return false;
      if (filter === 'active' && isPaused) return false;
      if (filter === 'paused' && !isPaused) return false;
      if (!search) return true;
      const term = search.toLowerCase();
      return (
        `${skater.firstName} ${skater.lastName}`.toLowerCase().includes(term) ||
        (skater.membershipId || '').toLowerCase().includes(term) ||
        (skater.club || '').toLowerCase().includes(term)
      );
    });
  }, [skaters, filter, search, seasonId]);

  const paidCount = useMemo(() => skaters.filter((skater) => skater.paid).length, [skaters]);
  const unpaidCount = useMemo(() => skaters.filter((skater) => !skater.paid).length, [skaters]);
  const duplicateCount = useMemo(() => skaters.filter((skater) => skater.isDuplicate).length, [skaters]);
  const pausedCount = useMemo(
    () => skaters.filter((skater) => (skater.pausedSeasons || []).includes(seasonId)).length,
    [skaters, seasonId]
  );
  const usfsVerifiedCount = useMemo(() => skaters.filter((skater) => skater.usfsVerified).length, [skaters]);
  const parentLinkedCount = useMemo(() => skaters.filter((skater) => skater.parentUsfsLinked).length, [skaters]);

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
        Skaters (Season Registration Status)
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <Chip label={`Total: ${skaters.length}`} size="small" />
        <Chip label={`Paid: ${paidCount}`} color="success" size="small" />
        <Chip label={`Unpaid: ${unpaidCount}`} color="warning" size="small" />
        <Chip
          label={`Duplicates: ${duplicateCount}`}
          color={duplicateCount > 0 ? 'error' : 'default'}
          size="small"
          variant="outlined"
        />
        {pausedCount > 0 && <Chip label={`Paused: ${pausedCount}`} color="default" size="small" />}
        <Chip 
          icon={<VerifiedUserIcon sx={{ fontSize: 14 }} />}
          label={`USFS Verified: ${usfsVerifiedCount}`} 
          color="info" 
          size="small" 
          variant="outlined"
        />
        <Chip 
          icon={<LinkIcon sx={{ fontSize: 14 }} />}
          label={`Parent USFS Linked: ${parentLinkedCount}`} 
          color="primary" 
          size="small" 
          variant="outlined"
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <FormControl sx={{ minWidth: 220 }}>
          <InputLabel>Season</InputLabel>
          <Select value={seasonId} label="Season" onChange={(e) => setSeasonId(e.target.value)}>
            {seasons.map((season) => (
              <MenuItem key={season.id} value={season.id}>
                {season.name || season.id}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 220 }} disabled={isInterclubAdmin()}>
          <InputLabel>Interclub</InputLabel>
          <Select value={interclubId} label="Interclub" onChange={(e) => setInterclubId(e.target.value)}>
            <MenuItem value="">
              <em>All interclubs</em>
            </MenuItem>
            {interclubs.map((interclub) => (
              <MenuItem key={interclub.id} value={interclub.id}>
                {interclub.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filter</InputLabel>
          <Select value={filter} label="Filter" onChange={(e) => setFilter(e.target.value)}>
            {filterOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 240 }}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Membership ID</TableCell>
              <TableCell>Club</TableCell>
              <TableCell>Level</TableCell>
              <TableCell>USFS</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSkaters.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No skaters found.
                </TableCell>
              </TableRow>
            ) : (
              filteredSkaters.map((skater) => {
                const isPaused = (skater.pausedSeasons || []).includes(seasonId);
                const isDuplicate = !!skater.isDuplicate;
                return (
                  <TableRow 
                    key={skater.id} 
                    hover
                    onClick={() => handleSkaterClick(skater)}
                    sx={{ 
                      cursor: 'pointer',
                      ...(isPaused ? { opacity: 0.6, bgcolor: 'action.hover' } : {}),
                      ...(isDuplicate ? { bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.12)' : 'rgba(255, 193, 7, 0.2)') } : {}),
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {skater.firstName} {skater.lastName}
                        {isDuplicate && (
                          <Tooltip
                            title={
                              skater.isDuplicateByName && skater.isDuplicateByMembershipId
                                ? `Same name and same USFS ID — group of ${(skater.duplicateGroup || []).length} skater(s); review and merge or remove duplicates`
                                : skater.isDuplicateByName
                                  ? `Same name — ${(skater.sameNameGroup || skater.duplicateGroup || []).length} record(s) with this name; review for duplicates`
                                  : `Same USFS ID — group of ${(skater.duplicateGroup || []).length} skater(s); review and merge or remove duplicates`
                            }
                          >
                            <Chip
                              label={skater.isDuplicateByName && !skater.isDuplicateByMembershipId ? 'Same name' : 'Duplicate'}
                              size="small"
                              color="warning"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{skater.membershipId || '—'}</TableCell>
                    <TableCell>{skater.club || '—'}</TableCell>
                    <TableCell>{skater.currentLevel || skater.startingLevel || '—'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {skater.usfsVerified && (
                          <Tooltip title="USFS Verified">
                            <Chip
                              icon={<VerifiedUserIcon sx={{ fontSize: 12 }} />}
                              label="Verified"
                              size="small"
                              color="success"
                              sx={{ fontSize: '0.65rem', height: 20 }}
                            />
                          </Tooltip>
                        )}
                        {skater.parentUsfsLinked && !skater.usfsVerified && (
                          <Tooltip title="Parent USFS Account Linked">
                            <Chip
                              icon={<LinkIcon sx={{ fontSize: 12 }} />}
                              label="Parent Linked"
                              size="small"
                              color="info"
                              variant="outlined"
                              sx={{ fontSize: '0.65rem', height: 20 }}
                            />
                          </Tooltip>
                        )}
                        {!skater.usfsVerified && !skater.parentUsfsLinked && (
                          <Tooltip title="No USFS Account Linked">
                            <LinkOffIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                          label={skater.paid ? 'Paid' : 'Unpaid'}
                          color={skater.paid ? 'success' : 'warning'}
                          size="small"
                        />
                        {isPaused && <Chip label="Paused" color="default" size="small" variant="outlined" />}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="View details">
                          <IconButton 
                            size="small" 
                            color="primary" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSkaterClick(skater);
                            }}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={isPaused ? 'Activate for season' : 'Pause for season'}>
                          <IconButton 
                            size="small" 
                            color={isPaused ? 'success' : 'default'} 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTogglePause(skater);
                            }}
                          >
                            {isPaused ? <PlayIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete skater">
                          <IconButton 
                            size="small" 
                            color="error" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteSkater(skater);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      )}

      {/* Skater Detail/Edit Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={handleCloseDetail}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        {selectedSkater && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  {editMode ? 'Edit Skater' : `${selectedSkater.firstName} ${selectedSkater.lastName}`}
                </Typography>
                <IconButton onClick={handleCloseDetail} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {selectedSkater.isDuplicate && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {selectedSkater.isDuplicateByName && selectedSkater.isDuplicateByMembershipId
                      ? 'Duplicate skater record (same name and same USFS ID)'
                      : selectedSkater.isDuplicateByName
                        ? 'Duplicate name — multiple records with this name'
                        : 'Duplicate skater record (same USFS ID)'}
                  </Typography>
                  {selectedSkater.isDuplicateByMembershipId && selectedSkater.membershipId && (
                    <Typography variant="body2">
                      Same USFS Membership ID ({selectedSkater.membershipId}) as{' '}
                      {(selectedSkater.duplicateGroup || []).length - 1} other record(s).
                    </Typography>
                  )}
                  {selectedSkater.isDuplicateByName && (
                    <Typography variant="body2" sx={{ mt: selectedSkater.isDuplicateByMembershipId ? 0.5 : 0 }}>
                      Same name as {(selectedSkater.sameNameGroup || selectedSkater.duplicateGroup || []).length - 1} other
                      record(s).
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Other skater record ID(s):{' '}
                    {[
                      ...new Set([
                        ...(selectedSkater.duplicateGroup || []),
                        ...(selectedSkater.sameNameGroup || []),
                      ].filter((id) => id !== selectedSkater.id)),
                    ].join(', ')}
                  </Typography>
                </Alert>
              )}
              {editMode ? (
                /* Edit Mode */
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="First Name"
                        value={editForm.firstName}
                        onChange={(e) => setEditForm(prev => ({ ...prev, firstName: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Last Name"
                        value={editForm.lastName}
                        onChange={(e) => setEditForm(prev => ({ ...prev, lastName: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="USFS Membership ID"
                        value={editForm.membershipId}
                        onChange={(e) => setEditForm(prev => ({ ...prev, membershipId: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Level</InputLabel>
                        <Select
                          value={editForm.currentLevel}
                          label="Level"
                          onChange={(e) => {
                            const newLevel = e.target.value;
                            setEditForm(prev => ({ ...prev, currentLevel: newLevel }));
                            if (newLevel !== 'Pre-Preliminary') {
                              setPrePrelimAcknowledged(false);
                            }
                          }}
                        >
                          <MenuItem value=""><em>Select a level</em></MenuItem>
                          {ALL_LEVELS.map(level => (
                            <MenuItem key={level} value={level}>{level}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>

                  {/* Pre-Preliminary Warning */}
                  {editForm.currentLevel === 'Pre-Preliminary' && (
                    selectedSkater.prePrelimAcknowledgedAt ? (
                      <Alert severity="info" sx={{ borderRadius: 2, '& .MuiAlert-message': { width: '100%' } }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                          Pre-Preliminary Level Acknowledgment on File
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          Acknowledged on{' '}
                          <strong>
                            {new Date(
                              selectedSkater.prePrelimAcknowledgedAt._seconds 
                                ? selectedSkater.prePrelimAcknowledgedAt._seconds * 1000 
                                : selectedSkater.prePrelimAcknowledgedAt
                            ).toLocaleString()}
                          </strong>
                          {selectedSkater.prePrelimAcknowledgedByEmail && (
                            <> by <strong>{selectedSkater.prePrelimAcknowledgedByEmail}</strong></>
                          )}
                        </Typography>
                      </Alert>
                    ) : isInterclubAdmin() ? (
                      <Alert severity="warning" sx={{ borderRadius: 2, '& .MuiAlert-message': { width: '100%' } }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                          Important: Pre-Preliminary Level Notice
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          Skaters at the <strong>Pre-Preliminary</strong> level may want to compete in Excel Series events, however doing so will make them
                          <strong> ineligible for our interclub championship series</strong>.
                        </Typography>
                        <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                          This acknowledgement must be made by the skater&apos;s parent/guardian through their account.
                        </Typography>
                      </Alert>
                    ) : (
                      <Alert severity="warning" sx={{ borderRadius: 2, '& .MuiAlert-message': { width: '100%' } }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                          Important: Pre-Preliminary Level Notice
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                          Skaters at the <strong>Pre-Preliminary</strong> level may want to compete in Excel Series events, however doing so will make them
                          <strong> ineligible for our interclub championship series</strong>.
                        </Typography>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={prePrelimAcknowledged}
                              onChange={(e) => setPrePrelimAcknowledged(e.target.checked)}
                              color="warning"
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              I understand that Pre-Preliminary skaters who compete in Excel Series will be ineligible for the interclub championship series
                            </Typography>
                          }
                        />
                      </Alert>
                    )
                  )}

                  <FormControl fullWidth>
                    <InputLabel>Club</InputLabel>
                    <Select
                      value={editForm.clubId}
                      label="Club"
                      onChange={(e) => setEditForm(prev => ({ ...prev, clubId: e.target.value }))}
                    >
                      <MenuItem value=""><em>Select a club</em></MenuItem>
                      {clubs.map(club => (
                        <MenuItem key={club.id} value={club.id}>{club.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Autocomplete
                    multiple
                    options={coaches}
                    getOptionLabel={(option) => {
                      const name = option.displayName || option.name || option.email;
                      const usfs = option.usfsMemberId ? ` • USFS# ${option.usfsMemberId}` : '';
                      const club = option.clubName ? ` • ${option.clubName}` : '';
                      const emailPart = option.email ? ` (${option.email})` : '';
                      return `${name}${emailPart}${usfs}${club}`.trim();
                    }}
                    value={(() => {
                      const norm = (s) => (s || '').trim().toLowerCase();
                      const resolved = editForm.coaches.map((c) => {
                        const match = coaches.find(
                          (coach) =>
                            coach.id === c.coachId ||
                            coach.coachDocId === c.coachId ||
                            coach.usfsMemberId === c.coachId ||
                            (c.email && coach.email === c.email)
                        );
                        return match || c;
                      });
                      const byName = new Map();
                      resolved.forEach((coach) => {
                        const name = norm(coach.displayName || coach.name || '');
                        const existing = byName.get(name);
                        if (!existing || (coach.email && !existing.email)) byName.set(name, coach);
                      });
                      return Array.from(byName.values());
                    })()}
                    onChange={(e, newValue) => {
                      setEditForm(prev => ({
                        ...prev,
                        coaches: newValue.map(coach => ({
                          coachId: coach.id,
                          displayName: coach.displayName || coach.name,
                          email: coach.email,
                        })),
                      }));
                    }}
                    renderInput={(params) => <TextField {...params} label="Coaches" />}
                    isOptionEqualToValue={(option, value) =>
                      option.id === (value.id ?? value.coachId) ||
                      option.coachDocId === value.coachId ||
                      option.usfsMemberId === value.coachId ||
                      (value.email && option.email === value.email)
                    }
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {option.displayName || option.name || 'Unknown'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {[option.usfsMemberId && `USFS# ${option.usfsMemberId}`, option.email, option.clubName].filter(Boolean).join(' • ')}
                          </Typography>
                        </Box>
                      </li>
                    )}
                  />
                </Box>
              ) : (
                /* View Mode */
                <Grid container spacing={2}>
                  {/* Skater ID */}
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="caption" color="text.secondary">Skater ID</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {selectedSkater.id}
                      </Typography>
                    </Paper>
                  </Grid>

                  {/* Membership Info */}
                  <Grid item xs={12}>
                    <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Skater Information
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BadgeIcon color="action" fontSize="small" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">USFS Membership ID</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {selectedSkater.membershipId || 'Not set'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BirthdayIcon color="action" fontSize="small" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">Date of Birth</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {(() => {
                            const bd = selectedSkater.dateOfBirth || selectedSkater.birthDate;
                            if (!bd) return 'Not set';
                            if (typeof bd === 'string') {
                              if (/^\d{4}-\d{2}-\d{2}$/.test(bd)) {
                                const [y, m, d] = bd.split('-');
                                return `${parseInt(m)}/${parseInt(d)}/${y}`;
                              }
                              return bd;
                            }
                            if (bd._seconds) {
                              const date = new Date(bd._seconds * 1000);
                              return `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${date.getUTCFullYear()}`;
                            }
                            return 'Not set';
                          })()}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ClubIcon color="action" fontSize="small" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">Club</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {selectedSkater.club || 'Not set'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon color="action" fontSize="small" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">Level</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {selectedSkater.currentLevel || selectedSkater.startingLevel || 'Not set'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Coaches */}
                  <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
                  <Grid item xs={12}>
                    <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Coaches
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    {selectedSkater.coaches && selectedSkater.coaches.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {selectedSkater.coaches.map((coach, index) => (
                          <Box 
                            key={index} 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1,
                              cursor: 'pointer',
                              p: 1,
                              borderRadius: 1,
                              '&:hover': { bgcolor: 'action.hover' }
                            }}
                            onClick={() => {
                              setDetailDialogOpen(false);
                              navigate(`/admin/coaches?search=${encodeURIComponent(coach.email || coach.displayName || coach.name || '')}`);
                            }}
                          >
                            <CoachIcon color="primary" fontSize="small" />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                {coach.displayName || coach.name || 'Unknown'}
                              </Typography>
                              {coach.email && (
                                <Typography variant="caption" color="text.secondary">
                                  {coach.email}
                                </Typography>
                              )}
                            </Box>
                            <OpenInNewIcon fontSize="small" color="action" />
                          </Box>
                        ))}
                      </Box>
                    ) : selectedSkater.coachName ? (
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1,
                          cursor: 'pointer',
                          p: 1,
                          borderRadius: 1,
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                        onClick={() => {
                          setDetailDialogOpen(false);
                          navigate(`/admin/coaches?search=${encodeURIComponent(selectedSkater.coachEmail || selectedSkater.coachName || '')}`);
                        }}
                      >
                        <CoachIcon color="primary" fontSize="small" />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                            {selectedSkater.coachName}
                          </Typography>
                          {selectedSkater.coachEmail && (
                            <Typography variant="caption" color="text.secondary">
                              {selectedSkater.coachEmail}
                            </Typography>
                          )}
                        </Box>
                        <OpenInNewIcon fontSize="small" color="action" />
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No coaches assigned</Typography>
                    )}
                  </Grid>

                  {/* Parent/Guardian (Owner) */}
                  <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
                  <Grid item xs={12}>
                    <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Parent / Guardian (Account Owner)
                    </Typography>
                  </Grid>
                  {ownerLoading ? (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">Loading...</Typography>
                      </Box>
                    </Grid>
                  ) : ownerInfo ? (
                    <>
                      <Grid item xs={12}>
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 2,
                            cursor: 'pointer',
                            p: 1.5,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' }
                          }}
                          onClick={() => {
                            setDetailDialogOpen(false);
                            navigate(`/admin/users?search=${encodeURIComponent(ownerInfo.email || ownerInfo.displayName || '')}`);
                          }}
                        >
                          <PersonIcon color="primary" />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                              {ownerInfo.displayName || 'Not set'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {ownerInfo.email || 'No email'}
                            </Typography>
                          </Box>
                          <Tooltip title="View user in User Management">
                            <OpenInNewIcon fontSize="small" color="action" />
                          </Tooltip>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Paper sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                          <Typography variant="caption" color="text.secondary">User ID</Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                            {selectedSkater.userId}
                          </Typography>
                        </Paper>
                      </Grid>
                    </>
                  ) : (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        {selectedSkater.userId ? 'Unable to load owner info' : 'No owner linked'}
                      </Typography>
                      {selectedSkater.userId && (
                        <Paper sx={{ p: 1.5, bgcolor: 'grey.50', mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">User ID</Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                            {selectedSkater.userId}
                          </Typography>
                        </Paper>
                      )}
                    </Grid>
                  )}

                  {/* Pre-Preliminary Acknowledgment */}
                  {(selectedSkater.currentLevel === 'Pre-Preliminary' || selectedSkater.startingLevel === 'Pre-Preliminary') && (
                    <>
                      <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
                      <Grid item xs={12}>
                        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Pre-Preliminary Acknowledgment
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        {selectedSkater.prePrelimAcknowledgedAt ? (
                          <Alert severity="info" icon={<WarningIcon />} sx={{ borderRadius: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Excel Series ineligibility acknowledged
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Acknowledged on {new Date(
                                selectedSkater.prePrelimAcknowledgedAt._seconds 
                                  ? selectedSkater.prePrelimAcknowledgedAt._seconds * 1000 
                                  : selectedSkater.prePrelimAcknowledgedAt
                              ).toLocaleString()}
                              {selectedSkater.prePrelimAcknowledgedByEmail && (
                                <> by {selectedSkater.prePrelimAcknowledgedByEmail}</>
                              )}
                            </Typography>
                            {/* Expandable original acknowledgment text */}
                            <Box sx={{ mt: 1 }}>
                              <Button
                                size="small"
                                onClick={() => setShowOriginalAck(!showOriginalAck)}
                                endIcon={showOriginalAck ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                sx={{ textTransform: 'none', p: 0 }}
                              >
                                {showOriginalAck ? 'Hide' : 'View'} original acknowledgment
                              </Button>
                              <Collapse in={showOriginalAck}>
                                <Paper variant="outlined" sx={{ mt: 1, p: 2, bgcolor: 'background.paper', borderColor: 'info.light' }}>
                                  <Typography variant="body2" sx={{ mb: 1 }}>
                                    Skaters at the <strong>Pre-Preliminary</strong> level may want to compete in Excel Series events, however doing so will make them
                                    <strong> ineligible for our interclub championship series</strong>.
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.dark' }}>
                                    ✓ I understand that Pre-Preliminary skaters who compete in Excel Series will be ineligible for the interclub championship series
                                  </Typography>
                                </Paper>
                              </Collapse>
                            </Box>
                          </Alert>
                        ) : (
                          <Alert severity="warning" sx={{ borderRadius: 2 }}>
                            <Typography variant="body2">
                              No acknowledgment on file for Pre-Preliminary level restrictions
                            </Typography>
                          </Alert>
                        )}
                      </Grid>
                    </>
                  )}

                  {/* USFS Link Status */}
                  <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
                  <Grid item xs={12}>
                    <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                      USFS Link Status
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Skater USFS Verified</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {selectedSkater.usfsVerified ? (
                        <Chip
                          icon={<VerifiedUserIcon sx={{ fontSize: 14 }} />}
                          label="Verified"
                          size="small"
                          color="success"
                        />
                      ) : (
                        <Chip
                          label="Not Verified"
                          size="small"
                          variant="outlined"
                          sx={{ color: 'text.secondary' }}
                        />
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Parent USFS Account Linked</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {selectedSkater.parentUsfsLinked ? (
                        <Chip
                          icon={<LinkIcon sx={{ fontSize: 14 }} />}
                          label="Linked"
                          size="small"
                          color="info"
                        />
                      ) : (
                        <Chip
                          icon={<LinkOffIcon sx={{ fontSize: 14 }} />}
                          label="Not Linked"
                          size="small"
                          variant="outlined"
                          sx={{ color: 'text.secondary' }}
                        />
                      )}
                    </Box>
                  </Grid>

                  {/* Registration Info */}
                  <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
                  <Grid item xs={12}>
                    <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Registration Info
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Payment Status</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={selectedSkater.paid ? 'Paid' : 'Unpaid'}
                        color={selectedSkater.paid ? 'success' : 'warning'}
                        size="small"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Created</Typography>
                    <Typography variant="body2">
                      {selectedSkater.createdAt 
                        ? new Date(
                            selectedSkater.createdAt._seconds 
                              ? selectedSkater.createdAt._seconds * 1000 
                              : selectedSkater.createdAt
                          ).toLocaleString()
                        : 'Unknown'}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
              {editMode ? (
                <>
                  <Button onClick={handleCancelEdit} variant="outlined">
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveEdit}
                    variant="contained"
                    disabled={saving || (editForm.currentLevel === 'Pre-Preliminary' && !isInterclubAdmin() && !prePrelimAcknowledged && !selectedSkater.prePrelimAcknowledgedAt)}
                    startIcon={saving ? <CircularProgress size={16} /> : null}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={handleEditClick}
                    variant="contained"
                    startIcon={<EditIcon />}
                  >
                    Edit Skater
                  </Button>
                  <Button onClick={handleCloseDetail} variant="outlined">
                    Close
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}
